import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Code,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconCheck, IconHistory, IconPencil, IconRestore, IconTrash, IconX } from "@tabler/icons-react";
import {
  apiDelete,
  apiFetch,
  apiPatch,
  apiPost,
  friendlyError,
  type ApiError,
} from "../../api/client.js";
import { type SnapshotSummary } from "./vmUtils.js";

interface SnapshotsModalProps {
  kind: "vm" | "container";
  vmid: number;
  name: string;
  opened: boolean;
  onClose: () => void;
  onMutated?: () => void;
}

const NAME_RE = /^[a-zA-Z0-9_][a-zA-Z0-9._-]*$/;

const MAX_SNAPSHOTS = 5;
const LIMIT_MESSAGE = `Snapshot limit reached (${MAX_SNAPSHOTS}) — delete a snapshot to create a new one.`;

const timeFmt = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatSnapTime(ts?: number): string {
  if (!ts) return "—";
  const d = new Date(ts * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  return timeFmt.format(d);
}

function isApiError(err: unknown): err is ApiError {
  return !!err && typeof err === "object" && "status" in err;
}

/*
 * Snapshots modal (Phase 14e). Shared by VM + CT detail pages. Lists
 * existing snapshots, creates new ones, and deletes old ones. Mirrors
 * the lifecycle of IsoPickerModal/ResizeVmModal: fetch on `opened`
 * flip, surface backend error codes via field errors or notifications,
 * call `onMutated` so the parent detail page refreshes its badge count.
 *
 * Create error mapping (matches the backend's response shapes):
 *   202 snapshot_in_progress   -> (success path) blue note, refresh after 3s
 *   400 invalid_request        -> map zod issues to field errors
 *   400 vmstate_requires_running -> field error on the RAM checkbox
 *   409 snapshot_name_taken    -> field error on the name input
 *   409 snapshot_limit_reached -> orange notification (5-snapshot cap)
 *   409 vm_locked              -> red notification
 *   502 pve_unavailable        -> red notification (pre-checks couldn't reach PVE)
 *   502 snapshot_failed        -> red notification with backend message
 *   401/403                    -> close modal (session expired)
 *
 * Delete error mapping:
 *   202 snapshot_delete_in_progress -> (success path) blue note, refresh after 3s
 *   404 orphaned               -> close modal
 *   502 snapshot_failed        -> red notification
 *
 * Rollback error mapping (Phase 17):
 *   200 rolled_back            -> green note + refresh
 *   202 rollback_in_progress   -> (success path) yellow note w/ backend message
 *   404 snapshot_not_found/orphaned -> red note + refresh
 *   409 vm_locked              -> yellow note w/ backend message
 *   502 rollback_failed        -> red notification w/ backend message
 *
 * Description edit error mapping (Phase 17):
 *   204                        -> green note + refresh
 *   400 invalid_request        -> field error under the textarea
 *   404 snapshot_not_found/orphaned -> red note + refresh
 *   502 update_failed          -> red notification w/ backend message
 */
export function SnapshotsModal({
  kind,
  vmid,
  name,
  opened,
  onClose,
  onMutated,
}: SnapshotsModalProps) {
  const resourceBase = kind === "vm" ? "vms" : "containers";

  const [snapshots, setSnapshots] = useState<SnapshotSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(false);

  const [snapName, setSnapName] = useState("");
  const [description, setDescription] = useState("");
  const [vmstate, setVmstate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [vmstateError, setVmstateError] = useState<string | undefined>(undefined);

  const [busySnap, setBusySnap] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editError, setEditError] = useState<string | undefined>(undefined);

  async function loadSnapshots() {
    setListLoading(true);
    setListError(false);
    try {
      const data = await apiFetch<{ snapshots: SnapshotSummary[] }>(
        `/api/${resourceBase}/${vmid}/snapshots`
      );
      setSnapshots(data.snapshots ?? []);
    } catch (err) {
      const e = err as ApiError;
      if (isApiError(e) && (e.status === 401 || e.status === 403 || e.status === 404)) {
        onClose();
        return;
      }
      setListError(true);
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    if (opened) void loadSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  function resetForm() {
    setSnapName("");
    setDescription("");
    setVmstate(false);
    setNameError(undefined);
    setVmstateError(undefined);
  }

  function validateName(value: string): string | undefined {
    if (!value) return "Name is required.";
    if (value.length > 40) return "Name must be 40 characters or fewer.";
    if (value.toLowerCase() === "current") return '"current" is a reserved name.';
    if (!NAME_RE.test(value)) {
      return "Use letters, numbers, . _ - only; first char must be a letter, number, or underscore.";
    }
    return undefined;
  }

  async function submit() {
    const err = validateName(snapName);
    if (err) {
      setNameError(err);
      return;
    }
    setNameError(undefined);
    setVmstateError(undefined);
    setSubmitting(true);

    const body: Record<string, unknown> = { name: snapName };
    if (description.trim()) body.description = description.trim();
    if (kind === "vm" && vmstate) body.vmstate = true;

    try {
      const res = await apiPost<{ status?: string; message?: string }>(
        `/api/${resourceBase}/${vmid}/snapshots`,
        body
      );
      if (res?.status === "snapshot_in_progress") {
        // 202 — snapshot is being created in the background; will appear in list shortly
        notifications.show({
          color: "blue",
          title: "Snapshot creating",
          message:
            res.message ?? "Snapshot is being created in the background; refresh in a moment.",
          autoClose: 8000,
        });
        setTimeout(() => void loadSnapshots(), 3000);
        onMutated?.();
        resetForm();
        return;
      }
      notifications.show({ color: "green", message: "Snapshot created." });
      resetForm();
      void loadSnapshots();
      onMutated?.();
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const e = err;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 401 || e.status === 403) {
        onClose();
      } else if (e.status === 409 && e.error === "snapshot_name_taken") {
        setNameError("A snapshot with this name already exists.");
      } else if (e.status === 409 && e.error === "snapshot_limit_reached") {
        notifications.show({ color: "orange", message: LIMIT_MESSAGE });
      } else if (e.status === 409 && e.error === "vm_locked") {
        notifications.show({
          color: "red",
          message: "Another operation is in progress; try again in a moment.",
        });
      } else if (e.status === 400 && e.error === "vmstate_requires_running") {
        setVmstateError("VM must be running to include RAM state.");
      } else if (e.status === 400 && e.error === "invalid_request") {
        const issues = (detail?.issues ?? []) as Array<{
          path?: string[];
          message?: string;
        }>;
        let mappedAny = false;
        for (const issue of issues) {
          const path = issue.path ?? [];
          const msg = issue.message ?? "Invalid value.";
          if (path.includes("name")) {
            setNameError(msg);
            mappedAny = true;
          } else if (path.includes("vmstate")) {
            setVmstateError(msg);
            mappedAny = true;
          }
        }
        if (!mappedAny) {
          notifications.show({
            color: "red",
            message: friendlyError(e, "Couldn't create snapshot."),
          });
        }
      } else if (e.status === 502 && e.error === "pve_unavailable") {
        notifications.show({
          color: "red",
          title: "Proxmox unavailable",
          message: "Couldn't reach Proxmox to take the snapshot. Try again in a moment.",
        });
      } else if (e.status === 502 && e.error === "snapshot_failed") {
        const msg =
          typeof detail?.message === "string"
            ? detail.message
            : "Proxmox reported the snapshot failed.";
        notifications.show({ color: "red", message: msg });
      } else {
        notifications.show({
          color: "red",
          message: friendlyError(e, "Couldn't create snapshot."),
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDelete(snap: SnapshotSummary) {
    modals.openConfirmModal({
      title: "Delete snapshot",
      centered: true,
      children: (
        <Text size="sm">
          Permanently delete snapshot <Code>{snap.name}</Code> from <b>{name}</b>? Any newer
          snapshots will be merged into it. <b>This cannot be undone.</b>
        </Text>
      ),
      labels: { confirm: "Delete snapshot", cancel: "Cancel" },
      confirmProps: { color: "red" },
      groupProps: { gap: "xs" },
      onConfirm: () => void doDelete(snap.name),
    });
  }

  async function doDelete(snapNameToDelete: string) {
    try {
      const res = await apiDelete<{ status?: string; message?: string }>(
        `/api/${resourceBase}/${vmid}/snapshots/${encodeURIComponent(snapNameToDelete)}`
      );
      if (res?.status === "snapshot_delete_in_progress") {
        // 202 — snapshot is being deleted in the background; will clear from list shortly
        notifications.show({
          color: "blue",
          title: "Snapshot deleting",
          message:
            res.message ?? "Snapshot deletion is running in the background; refresh in a moment.",
          autoClose: 8000,
        });
        setTimeout(() => void loadSnapshots(), 3000);
        onMutated?.();
        return;
      }
      notifications.show({ color: "green", message: "Snapshot deleted." });
      void loadSnapshots();
      onMutated?.();
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const e = err;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 401 || e.status === 403) {
        onClose();
      } else if (e.status === 404 && e.error === "orphaned") {
        onClose();
      } else if (e.status === 502 && e.error === "snapshot_failed") {
        const msg =
          typeof detail?.message === "string"
            ? detail.message
            : "Proxmox reported the delete failed.";
        notifications.show({ color: "red", message: msg });
      } else {
        notifications.show({
          color: "red",
          message: friendlyError(e, "Couldn't delete snapshot."),
        });
      }
    }
  }

  function confirmRollback(snap: SnapshotSummary) {
    const label = kind === "vm" ? "VM" : "container";
    modals.openConfirmModal({
      title: `Roll back to '${snap.name}'?`,
      centered: true,
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            The {label} will be reverted to the state captured in this snapshot.{" "}
            <b>Everything changed since — files, software, data — will be permanently lost.</b>
          </Text>
          <Text size="sm">
            {kind === "vm" && snap.vmstate
              ? "The VM will stop during rollback and restart automatically from its saved RAM state."
              : `The ${label} will stop during rollback and stay stopped afterwards — start it again from the dashboard.`}
          </Text>
          <Text size="sm" c="dimmed">
            This cannot be undone.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Roll back", cancel: "Cancel" },
      confirmProps: { color: "red" },
      groupProps: { gap: "xs" },
      onConfirm: () => void doRollback(snap.name),
    });
  }

  async function doRollback(snapNameToRollback: string) {
    setBusySnap(snapNameToRollback);
    try {
      const res = await apiPost<{ status?: string; message?: string }>(
        `/api/${resourceBase}/${vmid}/snapshots/${encodeURIComponent(snapNameToRollback)}/rollback`
      );
      if (res?.status === "rollback_in_progress") {
        // 202 — rollback is running in the background; guest restarts (or stays
        // stopped) when it completes
        notifications.show({
          color: "yellow",
          title: "Rollback in progress",
          message:
            res.message ??
            "Rollback is running in the background; refresh in a moment.",
          autoClose: 8000,
        });
      } else {
        notifications.show({
          color: "green",
          message: `Snapshot '${snapNameToRollback}' restored.`,
        });
      }
      void loadSnapshots();
      onMutated?.();
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const e = err;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 401 || e.status === 403) {
        onClose();
      } else if (
        e.status === 404 &&
        (e.error === "snapshot_not_found" || e.error === "orphaned")
      ) {
        notifications.show({ color: "red", message: "This snapshot no longer exists." });
        void loadSnapshots();
      } else if (e.status === 409 && e.error === "vm_locked") {
        notifications.show({
          color: "yellow",
          message:
            typeof detail?.message === "string"
              ? detail.message
              : "Another snapshot operation is in progress; try again in a moment.",
        });
      } else if (e.status === 502 && e.error === "rollback_failed") {
        const msg =
          typeof detail?.message === "string" ? detail.message : "Rollback failed.";
        notifications.show({ color: "red", message: msg });
      } else {
        notifications.show({ color: "red", message: friendlyError(e, "Rollback failed.") });
      }
    } finally {
      setBusySnap(null);
    }
  }

  function startEdit(snap: SnapshotSummary) {
    setEditing(snap.name);
    setEditText(snap.description ?? "");
    setEditError(undefined);
  }

  function cancelEdit() {
    setEditing(null);
    setEditError(undefined);
  }

  async function saveDescription(snapNameToEdit: string) {
    setEditError(undefined);
    setBusySnap(snapNameToEdit);
    try {
      await apiPatch(
        `/api/${resourceBase}/${vmid}/snapshots/${encodeURIComponent(snapNameToEdit)}`,
        { description: editText }
      );
      notifications.show({ color: "green", message: "Description updated." });
      setEditing(null);
      void loadSnapshots();
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const e = err;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 401 || e.status === 403) {
        onClose();
      } else if (
        e.status === 404 &&
        (e.error === "snapshot_not_found" || e.error === "orphaned")
      ) {
        notifications.show({ color: "red", message: "This snapshot no longer exists." });
        setEditing(null);
        void loadSnapshots();
      } else if (e.status === 400 && e.error === "invalid_request") {
        const issues = (detail?.issues ?? []) as Array<{
          path?: string[];
          message?: string;
        }>;
        const descIssue = issues.find((i) => (i.path ?? []).includes("description"));
        setEditError(descIssue?.message ?? "Description must be 1024 characters or fewer.");
      } else if (e.status === 502 && e.error === "update_failed") {
        const msg =
          typeof detail?.message === "string"
            ? detail.message
            : "Couldn't update description.";
        notifications.show({ color: "red", message: msg });
      } else {
        notifications.show({
          color: "red",
          message: friendlyError(e, "Couldn't update description."),
        });
      }
    } finally {
      setBusySnap(null);
    }
  }

  const atLimit = snapshots.length >= MAX_SNAPSHOTS;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${kind === "vm" ? "VM" : "Container"} snapshots — ${name}`}
      size="lg"
    >
      <Stack gap="md">
        <Stack gap="sm">
          <TextInput
            label="Snapshot name"
            placeholder="pre-release-checkpoint"
            value={snapName}
            onChange={(e) => {
              setSnapName(e.currentTarget.value);
              if (nameError) setNameError(undefined);
            }}
            error={nameError}
            maxLength={40}
            disabled={submitting}
          />
          <Textarea
            label="Description (optional)"
            placeholder="What state does this snapshot capture?"
            maxLength={1024}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={4}
            disabled={submitting}
          />
          {kind === "vm" && (
            <Checkbox
              checked={vmstate}
              onChange={(e) => {
                setVmstate(e.currentTarget.checked);
                if (vmstateError) setVmstateError(undefined);
              }}
              label="Include RAM state"
              description="Saves the VM's running memory so the snapshot can be restored with the OS still booted. The VM MUST be running; untick if it's stopped. The VM restarts automatically from its saved RAM state after a rollback."
              error={vmstateError}
              disabled={submitting}
            />
          )}
          {atLimit && (
            <Text size="xs" c="orange">
              {LIMIT_MESSAGE}
            </Text>
          )}
          <Button
            fullWidth
            loading={submitting}
            disabled={atLimit}
            onClick={() => void submit()}
          >
            Create snapshot
          </Button>
        </Stack>

        {listLoading ? (
          <Group justify="center" py="md">
            <Loader />
          </Group>
        ) : listError ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
            Couldn't load snapshots. Proxmox may be unreachable — close and try again.
          </Alert>
        ) : snapshots.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="md">
            No snapshots yet.
          </Text>
        ) : (
          <Stack gap="xs">
            <Group gap="xs">
              <IconHistory size={16} />
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {snapshots.length} snapshot{snapshots.length === 1 ? "" : "s"}
              </Text>
            </Group>
            {snapshots.map((snap) => {
              const isCurrent = snap.name.toLowerCase() === "current";
              return (
                <Group
                  key={snap.name}
                  justify="space-between"
                  align="flex-start"
                  wrap="nowrap"
                >
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap="xs">
                      <Code>{snap.name}</Code>
                      {isCurrent && (
                        <Text size="xs" c="dimmed">
                          live state
                        </Text>
                      )}
                    </Group>
                    {editing === snap.name ? (
                      <Stack gap={4}>
                        <Textarea
                          autosize
                          minRows={1}
                          maxRows={3}
                          maxLength={1024}
                          value={editText}
                          onChange={(e) => {
                            setEditText(e.currentTarget.value);
                            if (editError) setEditError(undefined);
                          }}
                          error={editError}
                        />
                        <Group gap="xs">
                          <Tooltip label="Save description" withArrow>
                            <ActionIcon
                              size="sm"
                              color="green"
                              variant="light"
                              onClick={() => void saveDescription(snap.name)}
                              aria-label={`Save description for ${snap.name}`}
                              disabled={busySnap === snap.name}
                            >
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Cancel" withArrow>
                            <ActionIcon
                              size="sm"
                              variant="default"
                              onClick={cancelEdit}
                              aria-label={`Cancel editing description for ${snap.name}`}
                              disabled={busySnap === snap.name}
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Stack>
                    ) : (
                      <Text size="xs" c={snap.description ? undefined : "dimmed"} component="div">
                        {snap.description || "No description."}
                      </Text>
                    )}
                    <Text size="xs" c="dimmed">
                      {formatSnapTime(snap.snaptime)}
                    </Text>
                  </Stack>
                  {!isCurrent && (
                    <Group gap="xs">
                      <Tooltip label="Roll back to this snapshot" position="left" withArrow>
                        <ActionIcon
                          color="orange"
                          variant="light"
                          onClick={() => confirmRollback(snap)}
                          aria-label={`Roll back to ${snap.name}`}
                          disabled={busySnap === snap.name}
                        >
                          <IconRestore size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Edit description" position="left" withArrow>
                        <ActionIcon
                          variant="default"
                          onClick={() => startEdit(snap)}
                          aria-label={`Edit description for ${snap.name}`}
                          disabled={busySnap === snap.name}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete snapshot" position="left" withArrow>
                        <ActionIcon
                          color="red"
                          variant="light"
                          onClick={() => confirmDelete(snap)}
                          aria-label={`Delete snapshot ${snap.name}`}
                          disabled={busySnap === snap.name}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
